import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  ReactNode,
} from 'react';
import type { Product } from '../types';

interface CartItem extends Product {
  QTD: number;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: Product & { QTD?: number } }
  | { type: 'REMOVE_FROM_CART'; payload: { id: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' };

interface CartContextType {
  cart: CartItem[];
  dispatch: React.Dispatch<CartAction>;
  cartSubtotal: number;
  cartIvaTotal: number;
  cartTotal: number;
  itemCount: number;
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartItem[], action: CartAction): CartItem[] => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.findIndex(
        (item) => item.ID === action.payload.ID
      );
      if (existingItemIndex !== -1) {
        const updatedCart = [...state];
        const quantity = action.payload.QTD || 1;
        updatedCart[existingItemIndex].QTD += quantity;
        return updatedCart;
      }
      return [...state, { ...action.payload, QTD: action.payload.QTD || 1 }];
    }

    case 'REMOVE_FROM_CART':
      return state.filter((item) => item.ID !== action.payload.id);

    case 'UPDATE_QUANTITY':
      return state.map((item) =>
        item.ID === action.payload.id
          ? { ...item, QTD: action.payload.quantity }
          : item
      );

    case 'CLEAR_CART':
      return [];

    default:
      return state;
  }
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Initialize cart from sessionStorage
  const getInitialCart = (): CartItem[] => {
    try {
      const stored = sessionStorage.getItem('cart');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const [cart, dispatch] = useReducer(cartReducer, [], getInitialCart);

  // Save cart to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Calculate totals
  const cartSubtotal = cart.reduce((sum, item) => {
    const price = Number(item.PRECO_PROMOCIONAL || item.PRECO) || 0;
    return sum + price * item.QTD;
  }, 0);

  const cartIvaTotal = cart.reduce((sum, item) => {
    const price = Number(item.PRECO_PROMOCIONAL || item.PRECO) || 0;
    const ivaRate = Number(item.IVA ?? item.iva ?? 0);
    return sum + (price * ivaRate / 100) * item.QTD;
  }, 0);

  const cartTotal = cartSubtotal + cartIvaTotal;

  // Calculate item count
  const itemCount = cart.reduce((count, item) => count + item.QTD, 0);

  // Helper functions
  const addToCart = (product: Product, quantity: number = 1): void => {
    dispatch({ type: 'ADD_TO_CART', payload: { ...product, QTD: quantity } });
  };

  const removeFromCart = (productId: string): void => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { id: productId } });
  };

  const updateQuantity = (productId: string, quantity: number): void => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: { id: productId, quantity },
      });
    }
  };

  const clearCart = (): void => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const value: CartContextType = {
    cart,
    dispatch,
    cartSubtotal,
    cartIvaTotal,
    cartTotal,
    itemCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export default CartContext;
